import { CurrencyValue } from '@/components/common'
import { Card } from '@/components/ui/card'
import { SETTLEMENT_COMPONENT_LABELS } from '../types'
import type { SettlementLineView } from '../types'

/**
 * One side of a settlement statement.
 *
 * <p>The rule this component exists to hold: a component that <em>could not be computed</em>
 * renders its reason and the words "Not computed", while a component that computed to zero
 * renders 0.00 with its basis. They must never look alike — "you are owed nothing" and
 * "nobody has configured this" are different things to tell somebody who has just left.
 */
export function SettlementComponentTable({ title, lines, total, currency }: {
  title: string
  lines: SettlementLineView[]
  total: number
  currency: string
}) {
  return (
    <Card>
      <h2 className="border-b border-slate-200 p-3 text-sm font-semibold">{title}</h2>
      <div className="overflow-x-auto overscroll-x-contain">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-slate-600">
            <tr>
              <th className="p-3 text-start font-medium">Component</th>
              <th className="p-3 text-end font-medium">Quantity</th>
              <th className="p-3 text-end font-medium">Rate</th>
              <th className="p-3 text-end font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td className="p-4 text-slate-600" colSpan={4}>Nothing here.</td></tr>
            )}
            {lines.map((line) => (
              <tr key={line.id} className="border-b border-slate-100 align-top">
                <td className="p-3">
                  <div>{SETTLEMENT_COMPONENT_LABELS[line.component] ?? line.label}</div>
                  {line.unavailableReason ? (
                    <p className="mt-1 max-w-2xl text-xs text-amber-700">
                      {line.unavailableReason}
                    </p>
                  ) : line.basis ? (
                    <p className="mt-1 max-w-2xl text-xs text-slate-600">{line.basis}</p>
                  ) : null}
                </td>
                <td className="p-3 text-end text-slate-600">{line.quantity ?? '—'}</td>
                <td className="p-3 text-end text-slate-600">{line.rate ?? '—'}</td>
                <td className="p-3 text-end">
                  {line.unavailableReason ? (
                    <span className="text-amber-700">Not computed</span>
                  ) : (
                    <CurrencyValue value={line.amount} currency={line.currency} />
                  )}
                </td>
              </tr>
            ))}
            <tr className="border-t border-slate-300 font-medium">
              <td className="p-3">Total</td>
              <td /><td />
              <td className="p-3 text-end">
                <CurrencyValue value={total} currency={currency} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  )
}
